/**
 * Cuánto vuelto hay que dar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL FALLO QUE ESTE ARCHIVO EXISTE PARA QUE NO VUELVA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo encontró un cajero cobrando de verdad. Una venta de $3,76 con el dólar a
 * 800 son 3.008 Bs exactos; como los bolívares se redondean, la pantalla le
 * pide al cliente **3.010**. El cliente da 3.020, y el vuelto son **10 Bs** —
 * lo que sobra de lo que se le pidió.
 *
 * Softfi calculaba otra cosa: pasaba los 3.020 a dólares (3,775), le restaba el
 * total exacto (3,76) y convertía el sobrante de vuelta a bolívares (12). Y
 * luego lo redondeaba OTRA VEZ, con lo que salían 15.
 *
 * Se pedía el número redondeado y se devolvía contra el número sin redondear.
 * Los dos redondeos existían y no se hablaban entre ellos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO VIVE EN SU PROPIO ARCHIVO Y NO DENTRO DE LA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque es la cuenta que decide cuánto dinero sale del cajón, y dentro de un
 * `useMemo` no se puede probar. Aquí sí: `vuelto.test.ts` recorre los casos con
 * los números exactos del cajero que lo reportó.
 */

import { aPrincipal, redondearMoneda, toNumber } from "@/lib/money";
import type { TenantCurrency } from "@/lib/types";

export type PagoParaVuelto = {
  /** Código de la moneda en que se entregó. */
  moneda: string;
  /** Lo entregado, EN esa moneda, tal como se tecleó. */
  monto: string | number;
};

/**
 * El vuelto a entregar, expresado en `monedaVuelto`.
 *
 * @param total    Lo que cuesta la venta, en la moneda de referencia.
 * @param pagos    Lo que el cliente puso, fila por fila.
 * @param recibido Todo lo recibido convertido a la moneda de referencia.
 */
export function calcularVuelto({
  total,
  recibido,
  pagos,
  monedaVuelto,
}: {
  total: number;
  recibido: number;
  pagos: PagoParaVuelto[];
  monedaVuelto: TenantCurrency;
}): number {
  const sobra = recibido - total;
  if (sobra <= 0) return 0;

  const tasa = toNumber(monedaVuelto.current_rate);
  if (tasa <= 0) return 0;

  const enEstaMoneda = pagos
    .filter((p) => p.moneda === monedaVuelto.currency_code)
    .reduce((suma, p) => suma + toNumber(p.monto), 0);

  /*
    El vuelto se da en una moneda con la que NO se pagó — «pagó en dólares y
    quiere el vuelto en bolívares».

    Aquí no hay un importe cobrado en esa moneda con el que comparar, así que se
    convierte el sobrante. Y ahí SÍ toca redondear: la conversión casi nunca cae
    en un importe que se pueda entregar en billetes.
  */
  if (enEstaMoneda <= 0) return redondearMoneda(sobra * tasa, monedaVuelto);

  /*
    Se pagó en esta moneda: el vuelto es una resta entre dos importes que ya son
    de ella.

      · Lo que pusieron en esta moneda            (3.020 Bs)
      · Menos lo que quedaba por cobrar en ella,  (3.010 Bs)
        redondeado IGUAL que cuando se le pidió

    ⚠️ El resultado NO se vuelve a redondear. Es la resta de dos importes que ya
    son cobrables en esa moneda, así que ya está bien — y redondearlo otra vez
    era exactamente lo que convertía 10 en 15.
  */
  const conLoDemas = recibido - aPrincipal(enEstaMoneda, monedaVuelto.current_rate);
  const debido = Math.max(0, total - conLoDemas);
  const debidoEnMoneda = redondearMoneda(debido * tasa, monedaVuelto);

  return Math.max(0, enEstaMoneda - debidoEnMoneda);
}

/**
 * Lo mismo, pero en la moneda de referencia: es lo que se enseña en grande.
 *
 * ⚠️ Se deriva del vuelto REAL y no al revés. Enseñar arriba «$0,02» y entregar
 * 10 Bs (que son $0,0125) es la contradicción que hacía dudar de las dos cifras
 * a la vez.
 */
export function vueltoEnPrincipal(vuelto: number, monedaVuelto: TenantCurrency): number {
  return aPrincipal(vuelto, monedaVuelto.current_rate);
}

// =============================================================================
//  EL VUELTO EN VARIAS MONEDAS (2026-10-12)
// =============================================================================

/** Una parte del vuelto: en qué moneda, con qué método y cuánto. */
export type ParteDelVuelto = {
  id: number;
  /** Código de la moneda en que se entrega esta parte. */
  moneda: string;
  /** El método con que sale (el efectivo, casi siempre). */
  metodo: string;
  /** Lo que se escribió. La ÚLTIMA parte no se escribe: se calcula. */
  monto: string;
};

/**
 * Cuánto se entrega en cada parte del vuelto.
 *
 * Lo pidió el dueño: «si alguien compró algo en 13,5 $ se le da 1 $ y 0,5 en
 * pesos». El cajero escribe lo que da en cada moneda menos en la última, y la
 * última es lo que falta del vuelto, en SU moneda.
 *
 * ⚠️ La última se calcula con `calcularVuelto`, igual que un vuelto de una sola
 * moneda, contando lo ya entregado como si fuera parte del total. Así hereda
 * la regla del archivo (el vuelto contra lo que se COBRÓ y no contra el precio
 * exacto), y con una sola parte da exactamente lo mismo que antes.
 *
 * Devuelve el monto de cada parte, en su moneda. Si lo escrito ya pasa del
 * vuelto, la última queda en cero y `deMas` dice por cuánto se pasó (en la
 * moneda de referencia).
 */
export function repartirVuelto({
  total,
  recibido,
  pagos,
  partes,
  monedas,
}: {
  total: number;
  recibido: number;
  pagos: PagoParaVuelto[];
  partes: Pick<ParteDelVuelto, "moneda" | "monto">[];
  monedas: TenantCurrency[];
}): { montos: number[]; deMas: number } {
  if (partes.length === 0) return { montos: [], deMas: 0 };
  const moneda = (codigo: string) => monedas.find((m) => m.currency_code === codigo);

  const escritas = partes.slice(0, -1).map((p) => Math.max(0, toNumber(p.monto)));
  const yaDado = partes
    .slice(0, -1)
    .reduce((suma, p, i) => suma + aPrincipal(escritas[i]!, moneda(p.moneda)?.current_rate ?? "0"), 0);

  const ultima = moneda(partes[partes.length - 1]!.moneda);
  const sobra = recibido - total;
  const deMas = Math.max(0, yaDado - sobra);
  const resto = ultima
    ? calcularVuelto({ total: total + yaDado, recibido, pagos, monedaVuelto: ultima })
    : 0;
  return { montos: [...escritas, resto], deMas };
}

/**
 * Lo que se propone escribir en una parte al dividir el vuelto: su parte
 * entera. De un vuelto de $1,50 se dan $1 y los 50 centavos en la otra moneda,
 * que es justo para lo que se divide.
 *
 * ⚠️ Si ya es entero, uno menos: de $4, $3 y el dólar que falta en la otra
 * moneda. Dejarlo igual proponía $4 y 0 en la otra —una parte vacía que no
 * deja cobrar— y se divide justo porque no alcanzan los billetes. Con 1 o
 * menos se deja como está: el cajero escribe cuánto da.
 */
export function parteEntera(monto: number, moneda: Pick<TenantCurrency, "decimals">): number {
  const entero = Math.floor(monto + 1e-9);
  const yaEsEntero = Math.abs(monto - entero) <= 10 ** -moneda.decimals / 2;
  if (!yaEsEntero) return entero > 0 ? entero : monto;
  return entero >= 2 ? entero - 1 : monto;
}
